import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../components/ui/tooltip";

import { openCreatePostModal } from "../../../features/post";
import { useSelectedAccounts } from "../../../entities/accounts";
import { cn } from "../../../lib/utils";
import { useTrackEvent } from "../../../libs/analytics";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { InstagramCampaignMenuEntry } from "../../../features/instagram-campaign/InstagramCampaignMenuEntry";

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "../../../components/ui/popover";
import "./menu.css";
import { Menu as LegacyMenu } from "./menu-legacy";
import SkedSocialIcon from "./icons/sked-social.svg";
import { getUser } from "../../../selectors/commonSelectors";
import { getAccounts } from "../../../selectors/skedCoreSelectors";
import { getLocalStoragePlannerSelectedAccount } from "../../../libs/storage/adapters";
import { COMMON_CLEAN_UP_LOADING } from "../../../constants/ActionTypes";
import { User } from "../../../features/user/types";
import { openSocialAccountConnectDialog } from "../../../features/social-account-connect/components/social-account-connect";
import { filterAccountsByPlatformType } from "../../../utils/accounts";
import { addInstagramSuggestionModal } from "../../planner/__redux-model";

import {
  UPGRADE_APPROVAL_VIEWED,
  UPGRADE_LABELS_REPORTING,
  UPGRADE_REPORT_BUILDER_VIEWED,
} from "../../../constants/Events";
import {
  SubMenuProvider,
  useSubMenuContext,
  type SubMenuKey,
} from "./hooks/sub-menu-context";
import { goToManagePlan } from "../../../utils/manage-plan";
import {
  hasApprovals,
  hasExternalApprovals,
  needsLabelsReportingUpsell,
} from "../../../utils/feature-gates";
import {
  ApprovalTooltipWrapper,
  openSupportForEnableApprovals,
} from "../../home/utils";
import { isUpsellRequiredForApproval } from "../../../utils/user";
import { slPermission } from "../../../utils/sl-permission";
import { hasGa4SettingsAccess } from "../../../features/account-groups-form/ga4-domain-gate";
import { AccountType } from "../../../shared/types/accounts";
import {
  entitlementFeatureToggle,
  FeatureName,
  localStorageFeatureToggle,
} from "../../../utils/feature-toggle-utils";
import { openCreatePostModalV2 } from "../../../features/post/bulk-create/bulk-create-post.actions";
import {
  registerBulkCreatePostModals,
  useAndOpenRequestedPosts,
} from "../../../features/post/bulk-create/bulk-create-post.model";
import { entitlementsPermission } from "../../../utils/entitlements-permission";
import {
  insightsPath,
  isInsightsSubpathActive,
  makeInsightsNavHandler,
  type InsightsPreviewRolloutState,
  useInsightsPreviewRolloutState,
} from "../../insights-preview/insights-preview-rollout";
import { useApprovalLinks } from "../../approvals/approval-queries";
import {
  INBOX_V2_GET_BOARDS_CACHE_KEY,
  realInboxV2DataSource,
} from "../../../@inbox-v2/data/real-api/data-source";
import { readInboxV2ApiCache } from "../../../@inbox-v2/data/real-api/api-cache";
import {
  getInboxV2BoardPath,
  INBOX_V2_BOARD_ROOT_PATH,
  isInboxV2BoardSubmenuPathActive,
  isInboxV2PinnedBoardPathActive,
} from "../../../@inbox-v2/board-routes";
import { subscribeInboxV2BoardsChanged } from "../../../@inbox-v2/board-events";
import type { InboxV2BoardsPayload } from "../../../@inbox-v2/types";
import { PAGES } from "../../../constants/Pages";
import {
  buildPostsRoute,
  getPostsViewFromPath,
} from "../../../components/Posts/posts-deep-link";
import { getStoredPostsView } from "../../../components/Posts/PostsTopnav/posts-session-state";

/**
 * Menu Link component with support for custom active state detection.
 * This is a local replacement for the deprecated router-bridge Link.
 */
interface LinkProps extends Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "onClick"
> {
  to?: string;
  checkActiveFunc?: (path: string) => boolean;
  children: React.ReactNode;
  className?: string;
  id?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  target?: string;
  "data-testid"?: string;
  "data-id"?: string;
}

export function isMenuLinkActive(currentPath: string, to?: string) {
  return to ? currentPath.includes(to.split("?")[0]) : false;
}

function Name({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        "menu-label font-inter w-full whitespace-nowrap text-sm font-medium leading-5 text-[#676571] no-underline",
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, className, children, checkActiveFunc, onClick, ...props },
  ref
) {
  const location = useLocation();

  const isActive = useMemo(() => {
    const currentPath = location.pathname;
    if (checkActiveFunc) {
      return checkActiveFunc(currentPath);
    }
    // Remove query param if exist
    return isMenuLinkActive(currentPath, to);
  }, [location.pathname, to, checkActiveFunc]);

  const combinedClassName =
    `${className || ""} ${isActive ? "active" : ""}`.trim();

  // If no `to` prop, render as a button-like anchor
  if (!to) {
    return (
      <a
        ref={ref}
        role="button"
        tabIndex={0}
        {...props}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.currentTarget.click();
          }
        }}
        className={combinedClassName}
      >
        {children}
      </a>
    );
  }

  return (
    <RouterLink
      aria-current={isActive ? "page" : undefined}
      ref={ref}
      to={to}
      {...props}
      onClick={onClick}
      className={combinedClassName}
    >
      {children}
    </RouterLink>
  );
});

interface MenuComponentProps {
  className?: string;
  url?: string;
}

interface MenuItemLinkProps extends LinkProps {
  icon?: JSX.Element;
  iconWrapperClassName?: string;
  labelClassName?: string;
}

interface MenuSectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

// Context for menu pinned state
const MenuPinnedContext = React.createContext<{
  isPinned: boolean;
  togglePinned: () => void;
}>({
  isPinned: true,
  togglePinned: () => {},
});

export const getSelectedAccountsForNewPost = (props?: {
  isPlannerPage: boolean;
  allAccounts: AccountType[];
  selectedAccounts: AccountType[];
}): AccountType[] => {
  const {
    isPlannerPage = false,
    allAccounts = [],
    selectedAccounts = [],
  } = props || {};

  if (!isPlannerPage) return selectedAccounts;

  const plannerSelectedAccount = getLocalStoragePlannerSelectedAccount();
  const selectedIGAccounts = selectedAccounts.filter(
    filterAccountsByPlatformType("IG")
  );

  // Try to match plannerSelectedAccount from selectedIGAccounts first
  if (selectedIGAccounts.length > 0 && plannerSelectedAccount) {
    const matched = selectedIGAccounts.filter(
      (account) => account._id === plannerSelectedAccount
    );
    if (matched.length > 0) {
      return matched;
    }
  }

  const IGAccounts = allAccounts.filter(filterAccountsByPlatformType("IG"));

  // Fall back to matching from all IG accounts if not found in selectedIGAccounts
  if (IGAccounts.length > 0 && plannerSelectedAccount) {
    const matched = IGAccounts.filter(
      (account) => account._id === plannerSelectedAccount
    );
    if (matched.length > 0) {
      return matched;
    }
  }

  // Fall back to first available IG account
  if (selectedIGAccounts.length > 0) return selectedIGAccounts.slice(0, 1);

  if (IGAccounts.length > 0) return IGAccounts.slice(0, 1);

  return [];
};

function getReportBuilderUpsellState(user: User, previewActive: boolean) {
  const upsellReportBuilder =
    !entitlementsPermission.hasCustomReportAccess(user);

  return {
    upsellReportBuilder,
    reportBuilderUrl: upsellReportBuilder
      ? insightsPath("/report-builder/upgrade?from=sidebar", previewActive)
      : insightsPath("/report-builder?from=sidebar", previewActive),
  };
}

// A component (not a render helper) so it can read the current location for the
// full-page-vs-client navigation decision.
function ReportBuilderV2MenuItem({
  upsellReportBuilder,
  reportBuilderUrl,
  user,
  trackEvent,
}: {
  upsellReportBuilder: boolean;
  reportBuilderUrl: string;
  user: User;
  trackEvent: ReturnType<typeof useTrackEvent>;
}) {
  return (
    <MenuItem
      icon={<MenuIcon name="file-chart-column" />}
      id="navigation-insights-v2-report-builder"
      to={reportBuilderUrl}
      checkActiveFunc={(path) =>
        isInsightsSubpathActive(path, "/report-builder")
      }
      onClick={makeInsightsNavHandler(reportBuilderUrl, () => {
        if (upsellReportBuilder) {
          trackEvent({
            eventName: UPGRADE_REPORT_BUILDER_VIEWED,
            useServices: ["AMP", "HS"],
            metadata: {
              plan: user?.plan,
            },
          });
        }
      })}
    >
      <UpsellTextWrapped text="Custom Reports" upsell={upsellReportBuilder} />
    </MenuItem>
  );
}

function MenuHeader({ onCreatePost }: { onCreatePost: () => void }) {
  const { isPinned } = React.useContext(MenuPinnedContext);
  return (
    <>
      <SkedSocial>
        <SkedSocialIcon />
        <Name className="pl-0 font-gabarito text-base font-bold leading-5 text-[#333333]">
          Sked Social
        </Name>
      </SkedSocial>
      <Tooltip open={isPinned ? false : undefined}>
        <TooltipTrigger asChild>
          <CreatePostButton
            data-testid="create-post-button"
            onClick={onCreatePost}
            id="navigation-create-post"
          >
            <MenuIcon name="file-pen" />
            <Name className="font-inter pl-0 text-sm font-semibold leading-5 text-white">
              Create Post
            </Name>
          </CreatePostButton>
        </TooltipTrigger>
        <TooltipContent side="right">Create Post</TooltipContent>
      </Tooltip>
    </>
  );
}

function MenuSection({ id, title, children, style }: MenuSectionProps) {
  return (
    <div id={id} className="flex w-full flex-col gap-1" style={style}>
      <div className="menu-group-heading">
        <span className="font-inter block whitespace-nowrap pl-3 text-[10px] font-medium leading-[14px] uppercase text-[#808080]">
          {title}
        </span>
        <hr className="hidden" />
      </div>
      <hr className="menu-mini-divider m-0 w-8 border-none" />
      <div className="menu-items flex w-full flex-col gap-1 rounded-lg bg-transparent p-0">
        {children}
      </div>
    </div>
  );
}

function MenuFooterToggle({
  isPinned,
  togglePinned,
}: {
  isPinned: boolean;
  togglePinned: () => void;
}) {
  const tooltipLabel = isPinned ? "Minimize menu" : "Expand sidebar";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <MenuToggleButton
          aria-label={tooltipLabel}
          aria-expanded={isPinned}
          data-testid="menu-footer-toggle"
          onClick={togglePinned}
          type="button"
        >
          <ToggleIcon isPinned={isPinned} />
        </MenuToggleButton>
      </TooltipTrigger>
      <TooltipContent side="right">{tooltipLabel}</TooltipContent>
    </Tooltip>
  );
}

const FlyoutContext = React.createContext(false);

function MenuIcon({
  name,
  className,
  ...props
}: {
  name: string;
  className?: string;
}) {
  return (
    <i
      {...props}
      aria-hidden="true"
      className={cn(
        "menu-font-awesome-icon fa-regular",
        `fa-${name}`,
        className
      )}
    />
  );
}

function NavigationSubmenu({
  menuKey,
  parent,
  children,
}: {
  menuKey: SubMenuKey;
  parent: React.ReactElement<MenuItemLinkProps>;
  children: React.ReactNode;
}) {
  const { isPinned } = React.useContext(MenuPinnedContext);
  const { openedSubMenu, setOpenedSubMenu, toggleSubMenu, flyout, setFlyout } =
    useSubMenuContext();
  const location = useLocation();
  const active =
    parent.props.checkActiveFunc?.(location.pathname) ??
    isMenuLinkActive(location.pathname, parent.props.to);
  const title = getTooltipText(parent.props.children);
  const id = `submenu-${menuKey}`;
  const trigger = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const suppressFocus = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const graceOrigin = useRef<{ x: number; y: number } | null>(null);
  const open = !isPinned && flyout === menuKey;
  const openRef = useRef(open);
  openRef.current = open;
  const cancelClose = () => {
    clearTimeout(closeTimer.current);
    graceOrigin.current = null;
  };
  const close = () => {
    cancelClose();
    if (openRef.current)
      setFlyout((current) => (current === menuKey ? null : current));
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(close, 250);
  };
  const show = () => {
    if (isPinned) return;
    cancelClose();
    // An existing flyout keeps its diagonal pointer corridor until the user
    // leaves it. This prevents crossing a neighbouring icon from stealing it.
    if (flyout && flyout !== menuKey) return;
    setFlyout(menuKey);
  };

  useEffect(() => {
    if (active) setOpenedSubMenu(menuKey);
    setFlyout(null);
    // Only a route change may reopen a manually closed selected section.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, active]);
  useEffect(() => {
    if (isPinned) setFlyout(null);
  }, [isPinned, setFlyout]);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!open) return;
    const move = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        trigger.current?.contains(target) ||
        content.current?.contains(target)
      ) {
        cancelClose();
        return;
      }
      const origin = graceOrigin.current;
      const bounds = content.current?.getBoundingClientRect();
      if (!origin || !bounds) return;
      const edge = bounds.left;
      const progress = (event.clientX - origin.x) / (edge - origin.x || 1);
      const upper = origin.y + (bounds.top - 8 - origin.y) * progress;
      const lower = origin.y + (bounds.bottom + 8 - origin.y) * progress;
      if (
        progress >= 0 &&
        progress <= 1 &&
        event.clientY >= upper &&
        event.clientY <= lower
      ) {
        // Refresh the delay while moving through the safe triangle.
        scheduleClose();
      } else {
        graceOrigin.current = null;
        close();
      }
    };
    const scroller = trigger.current?.closest("#grouped-menu");
    scroller?.addEventListener("scroll", close);
    document.addEventListener("pointermove", move);
    return () => {
      scroller?.removeEventListener("scroll", close);
      document.removeEventListener("pointermove", move);
    };
    // Handlers read the live refs; install only while this flyout is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parentLink = React.cloneElement(parent, {
    "aria-controls": id,
    "aria-expanded": isPinned ? openedSubMenu === menuKey : open,
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        parent.props.onClick?.(event);
        return;
      }
      close();
      if (isPinned && active) {
        event.preventDefault();
        toggleSubMenu(menuKey);
        return;
      }
      setOpenedSubMenu(menuKey);
      parent.props.onClick?.(event);
    },
  });

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <PopoverAnchor asChild>
        <div
          ref={trigger}
          className="menu-submenu-anchor"
          onPointerEnter={show}
          onPointerMove={show}
          onPointerLeave={(event) => {
            if (!open) return;
            graceOrigin.current = { x: event.clientX, y: event.clientY };
            scheduleClose();
          }}
          onFocus={() => {
            if (suppressFocus.current) {
              suppressFocus.current = false;
              return;
            }
            if (!isPinned) {
              cancelClose();
              setFlyout(menuKey);
            }
          }}
          onBlur={(event) => {
            if (!content.current?.contains(event.relatedTarget as Node))
              scheduleClose();
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              close();
              event.stopPropagation();
            }
            if (
              !isPinned &&
              (event.key === "ArrowRight" ||
                (open && event.key === "Tab" && !event.shiftKey))
            ) {
              event.preventDefault();
              content.current?.querySelector<HTMLElement>("a")?.focus();
            }
          }}
        >
          {parentLink}
        </div>
      </PopoverAnchor>
      {isPinned ? (
        <div
          id={id}
          className="menu-inline-submenu"
          hidden={openedSubMenu !== menuKey}
        >
          {children}
        </div>
      ) : (
        <PopoverContent
          side="right"
          align="start"
          sideOffset={12}
          collisionPadding={8}
          motion="none"
          widthMode="custom"
          className="menu-flyout"
          id={id}
          aria-label={title}
          onInteractOutside={(event) => {
            if (trigger.current?.contains(event.target as Node))
              event.preventDefault();
          }}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={() => {
            suppressFocus.current = true;
            trigger.current
              ?.querySelector<HTMLElement>("a")
              ?.focus({ preventScroll: true });
            suppressFocus.current = false;
            close();
          }}
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
          onFocus={cancelClose}
          onBlur={(event) => {
            if (
              !event.currentTarget.contains(event.relatedTarget as Node) &&
              !trigger.current?.contains(event.relatedTarget as Node)
            )
              close();
          }}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest("a")) close();
          }}
        >
          <div ref={content}>
            <div className="menu-flyout-heading">{title}</div>
            <FlyoutContext.Provider value>{children}</FlyoutContext.Provider>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}

function MenuComponent(props: MenuComponentProps) {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const accounts = useSelectedAccounts();
  const allAccounts = useSelector(getAccounts);
  const user: User = useSelector(getUser);
  const { data: stateLinks = [], isLoading: approvalLinksLoading } =
    useApprovalLinks();
  const [inboxReady, setInboxReady] = useState(false);
  const insightsRollout = useInsightsPreviewRolloutState();
  const showInboxComments = true;
  entitlementFeatureToggle.notifyUser(user);
  useAndOpenRequestedPosts({ dispatch, user });

  const handleCreatePost = () => {
    const isPlannerPage =
      window.location.pathname.startsWith("/dashboard/planner") ||
      window.location.pathname.startsWith("/dashboard/grid-preview");
    const selectedAccountsForNewPost = getSelectedAccountsForNewPost({
      isPlannerPage,
      allAccounts: allAccounts as AccountType[],
      selectedAccounts: accounts,
    });

    if (accounts.length === 0 && allAccounts.length === 0) {
      openSocialAccountConnectDialog();
      return;
    }

    if (allAccounts.length === 0) {
      dispatch(addInstagramSuggestionModal.open as any);

      return;
    }

    dispatch({ type: COMMON_CLEAN_UP_LOADING });
    if (entitlementFeatureToggle.get("bulk-create")) {
      dispatch(
        openCreatePostModalV2({
          accounts: selectedAccountsForNewPost,
        })
      );

      return;
    }
    dispatch(
      openCreatePostModal({
        accounts: selectedAccountsForNewPost,
      })
    );
  };
  // const handleUsersClick = () => {
  //   trackEvent({ eventName: USER_PAGE_NAVIGATION });
  // };

  useEffect(() => {
    /** ignore if user is not loadded yet */
    if (!user?.userId) {
      return;
    }

    sessionStorage.setItem("isInboxEnabled", "true");

    if (showInboxComments) {
      sessionStorage.setItem("isInboxCommentsEnabled", "true");
    } else {
      sessionStorage.removeItem("isInboxCommentsEnabled");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user?.userId, stateLinks.length]);

  useEffect(() => {
    registerBulkCreatePostModals();
  }, []);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [scrollEdges, setScrollEdges] = useState({
    above: false,
    below: false,
  });
  const updateScrollEdges = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const above = container.scrollTop > 1;
    const below =
      container.scrollHeight - container.clientHeight - container.scrollTop > 1;
    setScrollEdges((current) =>
      current.above === above && current.below === below
        ? current
        : { above, below }
    );
  }, []);

  useLayoutEffect(() => {
    updateScrollEdges();
    if (typeof ResizeObserver === "undefined") return;
    // Observe natural content height too: submenus and async boards can change
    // the overflow without changing the scroller's own dimensions.
    const observer = new ResizeObserver(updateScrollEdges);
    if (scrollContainerRef.current)
      observer.observe(scrollContainerRef.current);
    if (scrollContentRef.current) observer.observe(scrollContentRef.current);
    return () => observer.disconnect();
  }, [updateScrollEdges]);
  const interacted = useRef(false);
  const revealed = useRef(false);
  const initialPath = useRef(window.location.pathname);
  useEffect(() => {
    if (
      !user?.userId ||
      !inboxReady ||
      approvalLinksLoading ||
      revealed.current ||
      interacted.current
    )
      return;
    const frame = requestAnimationFrame(() => {
      if (
        interacted.current ||
        window.location.pathname !== initialPath.current
      )
        return;
      const container = scrollContainerRef.current;
      const selected =
        container?.querySelector<HTMLElement>(
          ".menu-sub-item.active:not([hidden] *)"
        ) ?? container?.querySelector<HTMLElement>(".menu-item.active");
      if (!container || !selected) return;
      revealed.current = true;
      const bounds = container.getBoundingClientRect();
      const item = selected.getBoundingClientRect();
      if (
        item.height &&
        (item.top < bounds.top || item.bottom > bounds.bottom)
      ) {
        container.scrollTop +=
          item.top < bounds.top
            ? item.top - bounds.top
            : item.bottom - bounds.bottom;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [user?.userId, inboxReady, approvalLinksLoading]);

  const upsellApproval =
    isUpsellRequiredForApproval(user) && stateLinks.length <= 0;

  const { upsellReportBuilder, reportBuilderUrl } = getReportBuilderUpsellState(
    user,
    insightsRollout.previewActive
  );
  const { isPinned, setPinned } = useMenuPinned(user?.userId);
  const handleTogglePinned = () => setPinned(!isPinned);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      MENU_MODAL_OFFSET_PROPERTY,
      isPinned ? MENU_EXPANDED_WIDTH : MENU_COLLAPSED_WIDTH
    );
    document.documentElement.style.setProperty(
      "--menu-width",
      isPinned ? MENU_EXPANDED_WIDTH : MENU_COLLAPSED_WIDTH
    );
    return () => {
      document.documentElement.style.removeProperty(MENU_MODAL_OFFSET_PROPERTY);
      document.documentElement.style.removeProperty("--menu-width");
    };
  }, [isPinned]);

  return (
    <MenuPinnedContext.Provider
      value={{ isPinned, togglePinned: handleTogglePinned }}
    >
      <SubMenuProvider>
        <MenuWrapper
          data-testid="main-menu-wrapper"
          className={cn(props.className, isPinned && "pinned")}
          onPointerDownCapture={() => {
            interacted.current = true;
          }}
          onKeyDownCapture={() => {
            interacted.current = true;
          }}
          onWheelCapture={() => {
            interacted.current = true;
          }}
        >
          <MenuContainer>
            <div className="menu-fixed-top" data-overflow={scrollEdges.above}>
              <MenuHeader onCreatePost={handleCreatePost} />

              <div className="menu-fixed-home">
                <div className="menu-items flex w-full flex-col gap-1 rounded-lg bg-transparent p-0">
                  <HomeMenuItem
                    to="/dashboard"
                    id="navigation-home"
                    icon={<MenuIcon name="house" />}
                    checkActiveFunc={(path) =>
                      path === "/dashboard" || path === "/dashboard/"
                    }
                    data-id="home-icon"
                  >
                    Sked Home
                  </HomeMenuItem>
                  {/*
                  SKE-4681. PINNED means this non-scrolling block, beside Home.
                  Inside `GroupSectionContainer` below it would scroll away,
                  which is the opposite of what pinned was asked for. The label
                  escalates with the register; the position never moves.
                */}
                  <InstagramCampaignMenuEntry
                    render={({ to, label, isUrgent }) => (
                      <HomeMenuItem
                        to={to}
                        id="navigation-instagram-campaign"
                        icon={
                          <i
                            aria-hidden="true"
                            className={cn(
                              "fa-regular fa-triangle-exclamation",
                              isUrgent && "text-red-700"
                            )}
                          />
                        }
                        /*
                         * The label colour has to be set as a DESCENDANT rule.
                         * `MenuItem`'s base carries `[&_.menu-label]:text-[#676571]`,
                         * and `important: true` in the Tailwind config puts
                         * `!important` on both, so the contest is purely
                         * specificity and the 0,2,0 descendant rule beats a plain
                         * `text-red-*` anywhere. Matching the variant lets
                         * tailwind-merge drop the grey instead.
                         *
                         * red-700, not red-600: the sidebar ground is #F2F1F9,
                         * where red-600 measures 4.45:1 and misses AA by a
                         * hair. red-700 is 6.87:1.
                         */
                        className={cn(
                          isUrgent && "[&_.menu-label]:text-red-700"
                        )}
                        data-id="instagram-campaign-icon"
                        data-urgent={isUrgent ? "true" : "false"}
                      >
                        {label}
                      </HomeMenuItem>
                    )}
                  />
                </div>
              </div>
            </div>
            <GroupSectionContainer
              id="grouped-menu"
              ref={scrollContainerRef}
              contentRef={scrollContentRef}
              onScroll={() => {
                interacted.current = true;
                updateScrollEdges();
              }}
            >
              <MenuSection
                id="grouped-menu-plan-and-collobrate"
                title="Plan & Collaborate"
              >
                <BrandKitsMenuItems />
                <MenuItem
                  to="/dashboard/idea-planner"
                  id="navigation-idea-planner"
                  icon={<MenuIcon name="lightbulb" data-id="workshop-icon" />}
                >
                  Ideas
                </MenuItem>

                <MenuItem
                  to="/dashboard/grid-preview"
                  id="navigation-planner"
                  icon={<MenuIcon name="mobile-phone" />}
                >
                  Grid Preview
                </MenuItem>
                <MenuItem
                  to="/dashboard/template-manager"
                  id="navigation-template-manager"
                  icon={<MenuIcon name="folder-grid" />}
                >
                  Templates
                </MenuItem>
              </MenuSection>

              <MenuSection id="grouped-menu-publish" title="Publish">
                <MenuItem
                  to="/dashboard/calendar"
                  id="navigation-calendar"
                  icon={<MenuIcon name="calendar-days" />}
                >
                  Calendar
                </MenuItem>
                <PostsMenuItems />
                <ApprovalsMenuItems
                  user={user}
                  upsellApproval={upsellApproval}
                  stateLinks={stateLinks}
                  trackEvent={trackEvent}
                />
                <MenuItem
                  to="/dashboard/library"
                  id="navigation-library"
                  icon={<MenuIcon name="books" />}
                >
                  Library
                </MenuItem>
              </MenuSection>

              <MenuSection
                id="grouped-menu-engage-and-convert"
                title="Engage & Convert"
              >
                <InboxV2MenuItems onReady={setInboxReady} />
                <MenuItem
                  to="/sked-link-sso"
                  target="_blank"
                  icon={<MenuIcon name="link" data-id="link-in-bio-icon" />}
                  id="navigation-sked-link"
                >
                  Link in bio
                </MenuItem>
              </MenuSection>

              <MenuSection id="grouped-menu-optimize" title="Optimize">
                <SocialListeningV2MenuItemsWithUpsell
                  previewActive={insightsRollout.previewActive}
                />
                <InsightsV2MenuItems
                  user={user}
                  trackEvent={trackEvent}
                  rollout={insightsRollout}
                />
                <ReportBuilderV2MenuItem
                  upsellReportBuilder={upsellReportBuilder}
                  reportBuilderUrl={reportBuilderUrl}
                  user={user}
                  trackEvent={trackEvent}
                />
              </MenuSection>
            </GroupSectionContainer>
            <div
              className="menu-fixed-bottom"
              data-overflow={scrollEdges.below}
            >
              <MenuFooterToggle
                isPinned={isPinned}
                togglePinned={handleTogglePinned}
              />
            </div>
          </MenuContainer>
        </MenuWrapper>
      </SubMenuProvider>
    </MenuPinnedContext.Provider>
  );
}

export const MenuUplift = MenuComponent;

export function Menu(props: MenuComponentProps) {
  // Console rollout flags take effect on reload, keeping navigation stable
  // throughout a session even if local storage changes in another tab.
  const [upliftEnabled] = useState(() =>
    localStorageFeatureToggle.get(FeatureName.SidenavUplift)
  );
  return upliftEnabled ? <MenuUplift {...props} /> : <LegacyMenu {...props} />;
}

const groupSectionContainerClassName = "menu-group-sections";

const GroupSectionContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    contentRef: React.Ref<HTMLDivElement>;
  }
>(function GroupSectionContainer(
  { children, className, contentRef, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(groupSectionContainerClassName, className)}
      {...props}
    >
      <div ref={contentRef} className="menu-group-content">
        {children}
      </div>
    </div>
  );
});

interface InsightsMenuItemsProps {
  user?: User;
  trackEvent: ReturnType<typeof useTrackEvent>;
  rollout: InsightsPreviewRolloutState;
}

const InsightsV2MenuItems = ({
  user,
  trackEvent,
  rollout,
}: InsightsMenuItemsProps) => {
  const upsellInsightLabel = needsLabelsReportingUpsell(user);
  const isUpsellAudience =
    !!user && !slPermission.hasSocialListeningAudience(user);
  // GA4 surfaces (Website Traffic + Tag Health) are behind the pre-launch
  // domain gate — the same check the insights pages themselves use.
  const canViewGa4 = hasGa4SettingsAccess(user);

  const insightsNav = makeInsightsNavHandler;

  const socialListeningUnavailableRoute =
    slPermission.getSocialListeningUnavailableRoute(
      insightsPath("/social-listening", rollout.previewActive),
      user
    );

  const brandSummaryTarget = insightsPath(
    "/analytics/overview",
    rollout.previewActive
  );
  const audienceTarget = isUpsellAudience
    ? socialListeningUnavailableRoute
    : insightsPath("/social-listening/audience", rollout.previewActive);
  const labelsTarget = insightsPath("/labels", rollout.previewActive);
  const adsTarget = insightsPath("/ads", rollout.previewActive);
  const websiteTrafficTarget = insightsPath(
    "/website-traffic",
    rollout.previewActive
  );
  const tagHealthTarget = insightsPath("/tag-health", rollout.previewActive);

  const labelsOnClick = () => {
    if (upsellInsightLabel) {
      trackEvent({
        eventName: UPGRADE_LABELS_REPORTING,
        useServices: ["AMP", "HS"],
      });
      goToManagePlan();
    }
    trackEvent({
      eventName: "nav-label-page",
      useServices: ["AMP"],
      metadata: {
        Location: "Side Nav",
      },
    });
  };

  return (
    <>
      <NavigationSubmenu
        menuKey="insightsV2"
        parent={
          <ParentMenuItem
            to={brandSummaryTarget}
            onClick={insightsNav(brandSummaryTarget)}
            icon={<MenuIcon name="telescope" data-id="insights-v2-icon" />}
            id="navigation-insights-v2"
            checkActiveFunc={(path) =>
              path === "/dashboard/insights/inbox-metrics" ||
              isInsightsSubpathActive(path, "/analytics") ||
              isInsightsSubpathActive(path, "/website-traffic") ||
              isInsightsSubpathActive(path, "/tag-health") ||
              isInsightsSubpathActive(path, "/labels") ||
              isInsightsSubpathActive(path, "/ads") ||
              isInsightsSubpathActive(path, "/social-listening/audience")
            }
          >
            Insights
          </ParentMenuItem>
        }
      >
        <SubMenuItem
          id="navigation-insights-v2-brand-summary"
          to={brandSummaryTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/analytics")
          }
          onClick={insightsNav(brandSummaryTarget)}
        >
          Brand Summary
        </SubMenuItem>
        <SubMenuItem
          id="navigation-social-listening-v2-audience"
          to={audienceTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/social-listening/audience")
          }
          onClick={insightsNav(audienceTarget)}
        >
          <UpsellTextWrapped text="Audience" upsell={isUpsellAudience} />
        </SubMenuItem>
        {localStorageFeatureToggle.get(FeatureName.InboxInsights) && (
          <SubMenuItem
            id="navigation-insights-inbox-metrics"
            to="/dashboard/insights/inbox-metrics"
          >
            Inbox Metrics
          </SubMenuItem>
        )}
        <SubMenuItem
          id="navigation-insights-v2-labels"
          to={upsellInsightLabel ? undefined : labelsTarget}
          onClick={
            upsellInsightLabel
              ? labelsOnClick
              : insightsNav(labelsTarget, labelsOnClick)
          }
        >
          <UpsellTextWrapped text="Labels" upsell={upsellInsightLabel} />
        </SubMenuItem>
        <SubMenuItem
          id="navigation-insights-v2-paid-ads"
          to={adsTarget}
          checkActiveFunc={(path) => isInsightsSubpathActive(path, "/ads")}
          onClick={insightsNav(adsTarget)}
        >
          <UpsellTextWrapped text="Paid Social" upsell={false} />
        </SubMenuItem>
        {canViewGa4 && (
          <SubMenuItem
            id="navigation-insights-v2-website-traffic"
            to={websiteTrafficTarget}
            checkActiveFunc={(path) =>
              isInsightsSubpathActive(path, "/website-traffic")
            }
            onClick={insightsNav(websiteTrafficTarget)}
          >
            Website Traffic
          </SubMenuItem>
        )}
        {canViewGa4 && (
          <SubMenuItem
            id="navigation-insights-v2-tag-health"
            to={tagHealthTarget}
            checkActiveFunc={(path) =>
              isInsightsSubpathActive(path, "/tag-health")
            }
            onClick={insightsNav(tagHealthTarget)}
          >
            Tag Health
          </SubMenuItem>
        )}
      </NavigationSubmenu>
    </>
  );
};

const SocialListeningV2MenuItemsWithUpsell = ({
  previewActive,
}: {
  previewActive: boolean;
}) => {
  const user = useSelector(getUser);

  const isSocialListeningEnabled = slPermission.enabledSocialListening(user);
  const socialListeningUnavailableRoute =
    slPermission.getSocialListeningUnavailableRoute(
      insightsPath("/social-listening", previewActive),
      user
    );
  const isUpsellSentiment = !slPermission.hasSocialListeningSentiment(user);
  const isUpsellKeyword = !slPermission.hasSocialListeningKeyword(user);

  const insightsNav = makeInsightsNavHandler;

  if (!isSocialListeningEnabled) {
    return (
      <ParentMenuItem
        to={socialListeningUnavailableRoute}
        icon={<MenuIcon name="headphones" data-id="social-listening-v2-icon" />}
        id="navigation-social-listening-v2"
        onClick={insightsNav(socialListeningUnavailableRoute)}
      >
        <UpsellTextWrapped text="Social Listening" upsell />
      </ParentMenuItem>
    );
  }

  const summaryTarget = insightsPath(
    "/social-listening/summary",
    previewActive
  );
  const sentimentTarget = isUpsellSentiment
    ? socialListeningUnavailableRoute
    : insightsPath("/social-listening/sentiment", previewActive);
  const competitorsTarget = insightsPath(
    "/social-listening/competitors",
    previewActive
  );
  const keywordTarget = isUpsellKeyword
    ? socialListeningUnavailableRoute
    : insightsPath("/social-listening/keyword", previewActive);

  return (
    <>
      <NavigationSubmenu
        menuKey="socialListeningV2"
        parent={
          <ParentMenuItem
            icon={
              <MenuIcon name="headphones" data-id="social-listening-v2-icon" />
            }
            id="navigation-social-listening-v2"
            to={summaryTarget}
            onClick={insightsNav(summaryTarget)}
            checkActiveFunc={(path) =>
              isInsightsSubpathActive(path, "/social-listening") &&
              !isInsightsSubpathActive(path, "/social-listening/audience")
            }
          >
            Social Listening
          </ParentMenuItem>
        }
      >
        <SubMenuItem
          id="navigation-social-listening-v2-summary"
          to={summaryTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/social-listening/summary")
          }
          onClick={insightsNav(summaryTarget)}
        >
          Summary
        </SubMenuItem>
        <SubMenuItem
          id="navigation-social-listening-v2-sentiment"
          to={sentimentTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/social-listening/sentiment")
          }
          onClick={insightsNav(sentimentTarget)}
        >
          <UpsellTextWrapped text="Sentiment" upsell={isUpsellSentiment} />
        </SubMenuItem>
        <SubMenuItem
          id="navigation-social-listening-v2-competitors"
          to={competitorsTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/social-listening/competitors")
          }
          onClick={insightsNav(competitorsTarget)}
        >
          Competitors
        </SubMenuItem>
        <SubMenuItem
          id="navigation-social-listening-v2-keyword"
          to={keywordTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/social-listening/keyword")
          }
          onClick={insightsNav(keywordTarget)}
        >
          <UpsellTextWrapped text="Keywords" upsell={isUpsellKeyword} />
        </SubMenuItem>
      </NavigationSubmenu>
    </>
  );
};

function InboxV2MenuItems({ onReady }: { onReady: (ready: boolean) => void }) {
  const [pinnedBoards, setPinnedBoards] = useState<
    Array<{ id: string; name: string; pinned: boolean }>
  >(() => getPinnedInboxV2Boards());
  const pinnedBoardIds = useMemo(
    () => new Set(pinnedBoards.map((board) => board.id)),
    [pinnedBoards]
  );
  const refreshPinnedBoards = useCallback(
    (forceNoCache = false) => {
      let cancelled = false;

      realInboxV2DataSource
        .getBoards({ forceNoCache })
        .then((payload) => {
          if (cancelled) {
            return;
          }

          setPinnedBoards(
            payload.boards.filter((board) => Boolean(board.pinned))
          );
        })
        .catch(() => {
          if (!cancelled) {
            setPinnedBoards([]);
          }
        })
        .finally(() => {
          if (!cancelled) onReady(true);
        });

      return () => {
        cancelled = true;
      };
    },
    [onReady]
  );

  useEffect(() => {
    return refreshPinnedBoards();
  }, [refreshPinnedBoards]);

  useEffect(
    () =>
      subscribeInboxV2BoardsChanged(() => {
        refreshPinnedBoards(true);
      }),
    [refreshPinnedBoards]
  );

  return (
    <>
      <NavigationSubmenu
        menuKey="inboxV2"
        parent={
          <ParentMenuItem
            icon={<MenuIcon name="inbox" />}
            id="navigation-inbox-v2"
            to={INBOX_V2_BOARD_ROOT_PATH}
            checkActiveFunc={(path) => path.includes("/dashboard/inbox-v2")}
          >
            Inbox
          </ParentMenuItem>
        }
      >
        <SubMenuItem
          id="navigation-inbox-v2-board"
          to={INBOX_V2_BOARD_ROOT_PATH}
          checkActiveFunc={(path) =>
            isInboxV2BoardSubmenuPathActive(path, pinnedBoardIds)
          }
        >
          Board
        </SubMenuItem>
        {pinnedBoards.map((board) => (
          <SubMenuItem
            id={`navigation-inbox-v2-board-${board.id}`}
            key={board.id}
            to={getInboxV2BoardPath(board.id)}
            checkActiveFunc={(path) =>
              isInboxV2PinnedBoardPathActive(path, board.id)
            }
          >
            {board.name}
          </SubMenuItem>
        ))}
        <SubMenuItem
          id="navigation-inbox-v2-reply-templates"
          to="/dashboard/inbox-v2/reply-templates"
          checkActiveFunc={(path) =>
            path.includes("/dashboard/inbox-v2/reply-templates")
          }
        >
          Reply Templates
        </SubMenuItem>
        <SubMenuItem
          id="navigation-inbox-v2-tags"
          to="/dashboard/inbox-v2/tags"
          checkActiveFunc={(path) => path.includes("/dashboard/inbox-v2/tags")}
        >
          Tags
        </SubMenuItem>
      </NavigationSubmenu>
    </>
  );
}

function getPinnedInboxV2Boards() {
  return (
    readInboxV2ApiCache<InboxV2BoardsPayload>(
      INBOX_V2_GET_BOARDS_CACHE_KEY
    )?.boards.filter((board) => Boolean(board.pinned)) ?? []
  );
}

const BrandKitsMenuItems = () => {
  return (
    <>
      <NavigationSubmenu
        menuKey="brandKits"
        parent={
          <ParentMenuItem
            icon={<MenuIcon name="bullhorn" data-id="brand-kits-icon" />}
            id="navigation-brand-kits"
            to="/dashboard/brand-kits/brand-description"
            checkActiveFunc={(path) => {
              return (
                path.includes("/dashboard/brand-kits") ||
                path.includes("/dashboard/workshop/labels")
              );
            }}
          >
            Brand Kit
          </ParentMenuItem>
        }
      >
        <SubMenuItem
          id="navigation-brand-kits-description"
          to="/dashboard/brand-kits/brand-description"
          checkActiveFunc={(path) => {
            return path.includes("/brand-kits/brand-description");
          }}
        >
          Brand Description
        </SubMenuItem>
        <SubMenuItem
          id="navigation-brand-kits-tov"
          to="/dashboard/brand-kits/tone-of-voice"
          checkActiveFunc={(path) => {
            return path.includes("/brand-kits/tone-of-voice");
          }}
        >
          Tone of Voice
        </SubMenuItem>
        <SubMenuItem
          id="navigation-brand-kits-instructions"
          to="/dashboard/brand-kits/instructions"
          checkActiveFunc={(path) => {
            return path.includes("/brand-kits/instructions");
          }}
        >
          Instructions
        </SubMenuItem>
        <SubMenuItem
          id="navigation-brand-kits-labels"
          to="/dashboard/workshop/labels"
          checkActiveFunc={(path) => {
            return path.includes("/dashboard/workshop/labels");
          }}
        >
          Labels
        </SubMenuItem>
      </NavigationSubmenu>
    </>
  );
};

const ApprovalsMenuItems = ({
  user,
  upsellApproval,
  stateLinks,
  trackEvent,
}: {
  user: User;
  upsellApproval: boolean;
  stateLinks: { length: number };
  trackEvent: ReturnType<typeof useTrackEvent>;
}) => {
  const canAccessExternalApprovals =
    hasExternalApprovals(user) && !user.preferences?.hideReviewLink;

  if (user.preferences?.hideReviewLink && !hasApprovals(user)) {
    return null;
  }

  if (hasApprovals(user)) {
    return (
      <>
        <NavigationSubmenu
          menuKey="approvals"
          parent={
            <ParentMenuItem
              icon={<MenuIcon name="comment-check" />}
              id="navigation-approvals"
              to="/dashboard/approvals/board"
              checkActiveFunc={(path) => path.includes("/dashboard/approvals")}
            >
              Approvals
            </ParentMenuItem>
          }
        >
          <SubMenuItem
            id="navigation-approvals-board"
            to="/dashboard/approvals/board"
            checkActiveFunc={(path) =>
              path.includes("/dashboard/approvals/board")
            }
          >
            Board
          </SubMenuItem>
          {canAccessExternalApprovals && (
            <SubMenuItem
              id="navigation-approvals-links-themes"
              to="/dashboard/approvals/links-themes"
              checkActiveFunc={(path) =>
                path.includes("/dashboard/approvals/links-themes")
              }
            >
              Links &amp; themes
            </SubMenuItem>
          )}
        </NavigationSubmenu>
      </>
    );
  }

  return (
    <ApprovalTooltipWrapper show={!hasApprovals(user) && !upsellApproval}>
      <MenuItem
        data-testid="approvalsOpenModalButton"
        to="/dashboard/approvals"
        onClick={() => {
          if (upsellApproval) {
            trackEvent({
              eventName: UPGRADE_APPROVAL_VIEWED,
              useServices: ["AMP", "HS"],
            });
            goToManagePlan();
          } else if (!hasApprovals(user) && stateLinks.length === 0) {
            openSupportForEnableApprovals();
          }
        }}
        icon={<MenuIcon name="comment-check" />}
        id="navigation-approvals"
      >
        <UpsellTextWrapped text="Approvals" upsell={upsellApproval} />
      </MenuItem>
    </ApprovalTooltipWrapper>
  );
};

const PostsMenuItems = () => {
  const location = useLocation();
  const postsView =
    getPostsViewFromPath(location.pathname) ?? getStoredPostsView();

  return (
    <>
      <NavigationSubmenu
        menuKey="posts"
        parent={
          <ParentMenuItem
            icon={<MenuIcon name="newspaper" />}
            id="navigation-posts"
            to={buildPostsRoute(PAGES.DRAFT, postsView)}
            checkActiveFunc={(path) =>
              path.includes("/dashboard/schedules/list/") ||
              path.includes("/dashboard/schedules/table/")
            }
          >
            Posts
          </ParentMenuItem>
        }
      >
        <SubMenuItem
          id="navigation-drafts"
          to={buildPostsRoute(PAGES.DRAFT, postsView)}
          checkActiveFunc={(path) => path.endsWith("/drafts")}
        >
          Drafts
        </SubMenuItem>
        <SubMenuItem
          id="navigation-upcoming"
          to={buildPostsRoute(PAGES.UPCOMING, postsView)}
          checkActiveFunc={(path) => path.endsWith("/active")}
        >
          Upcoming
        </SubMenuItem>
        <SubMenuItem
          id="navigation-archives"
          to={buildPostsRoute(PAGES.ARCHIVE, postsView)}
          checkActiveFunc={(path) => path.endsWith("/inactive")}
        >
          Archive
        </SubMenuItem>
      </NavigationSubmenu>
    </>
  );
};

const MENU_MODAL_OFFSET_PROPERTY = "--menu-modal-offset";
const MENU_COLLAPSED_WIDTH = "60px";
const MENU_EXPANDED_WIDTH = "216px";

function useMenuPinned(userId?: string) {
  const key = userId ? `sked-menu-expanded:${userId}` : null;
  const read = () => {
    try {
      return key ? localStorage.getItem(key) !== "false" : true;
    } catch {
      return true;
    }
  };
  const [preference, setPreference] = useState(() => ({
    key,
    expanded: read(),
  }));
  const isPinned = preference.key === key ? preference.expanded : read();
  const setPinned = (expanded: boolean) => {
    setPreference({ key, expanded });
    if (key) {
      try {
        localStorage.setItem(key, String(expanded));
      } catch {
        /* Storage can be unavailable. */
      }
    }
  };
  return { isPinned, setPinned };
}

// Extract text content for tooltip (outside component for performance)
const getTooltipText = (node: React.ReactNode): string => {
  if (typeof node === "string") return node;
  if (React.isValidElement(node)) {
    const childContent = node.props?.children;
    if (typeof childContent === "string") return childContent;
    if (node.props?.text) return node.props.text;
  }
  return "";
};

const MenuItemLink = ({
  icon,
  children,
  iconWrapperClassName,
  labelClassName,
  ...props
}: MenuItemLinkProps) => {
  const dispatch = useDispatch();
  const { isPinned } = React.useContext(MenuPinnedContext);
  const inFlyout = React.useContext(FlyoutContext);
  const showTooltip = !isPinned && !inFlyout && !props["aria-controls"];

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    dispatch({ type: COMMON_CLEAN_UP_LOADING });
    props.onClick?.(e);
  };

  const tooltipText = getTooltipText(children);

  const linkContent = (
    <Link
      {...props}
      aria-label={tooltipText || undefined}
      onClick={handleClick}
    >
      {icon && (
        <span
          className={cn(
            "menu-icon flex shrink-0 items-center justify-center",
            iconWrapperClassName
          )}
        >
          {icon}
        </span>
      )}
      <Name className={labelClassName}>{children}</Name>
      {!isPinned &&
        !inFlyout &&
        React.isValidElement(children) &&
        children.props.upsell && (
          <span className="menu-collapsed-upsell" aria-label="Upgrade required">
            <i className="fa-solid fa-rocket" aria-hidden="true" />
          </span>
        )}
      {inFlyout && <MenuIcon name="check" className="menu-current-check" />}
    </Link>
  );

  if (showTooltip && tooltipText) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right">{tooltipText}</TooltipContent>
      </Tooltip>
    );
  }

  return linkContent;
};

const UpsellTextWrapped = ({
  text,
  upsell,
}: {
  text: string;
  upsell: boolean;
}) => {
  return upsell ? (
    <InsightLabelWrap>
      {text}
      <span className="menu-upsell-badge ml-3 flex h-5 w-5 items-center justify-center rounded-[15px] bg-[#4F31FF] text-xs font-semibold leading-[25px] text-black">
        <i
          className="fa-solid fa-rocket menu-upsell-icon text-white"
          aria-hidden="true"
        />
      </span>
    </InsightLabelWrap>
  ) : (
    <>{text}</>
  );
};

function MenuContainer({
  children,
  className,
}: React.HTMLAttributes<HTMLElement>) {
  return <nav className={cn("menu-container", className)}>{children}</nav>;
}

const CreatePostButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function CreatePostButton({ children, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label="Create Post"
      className={cn(
        "menu-create-post-button relative flex h-7 w-full shrink-0 cursor-pointer items-center justify-start gap-2 whitespace-nowrap text-left rounded-lg bg-[#ED207C] px-3 py-1 text-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-[width,padding,gap,filter,box-shadow] duration-[var(--menu-transition-duration)] ease-[var(--menu-transition-ease)] hover:brightness-105 hover:shadow-[0_2px_4px_rgba(16,24,40,0.08)] motion-reduce:transition-none [&_svg]:max-h-[18px] [&_svg]:max-w-[18px] [&_svg]:shrink-0 [&_svg]:overflow-visible",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
});

function InsightLabelWrap({ children }: { children: React.ReactNode }) {
  return (
    <span className="menu-upsell-label flex items-center">{children}</span>
  );
}

function SkedSocial({ children }: { children: React.ReactNode }) {
  return <div className="menu-brand">{children}</div>;
}

function ToggleIcon({ isPinned }: { isPinned: boolean }) {
  return (
    <span className="menu-toggle-icon pointer-events-none flex items-center justify-center text-[#B3B3B3] transition-colors duration-150 ease-in-out">
      <i
        aria-hidden="true"
        className={cn(
          "fa-sidebar text-base leading-none",
          isPinned ? "fa-solid" : "fa-regular"
        )}
      />
    </span>
  );
}

const MenuToggleButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function MenuToggleButton(props, ref) {
  // eslint-disable-next-line react/prop-types
  const { children, className, ...buttonProps } = props;

  return (
    <button
      ref={ref}
      className={cn(
        "menu-footer-toggle flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-transparent p-1 transition-colors duration-150 ease-in-out hover:bg-[#FCFAFF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F31FF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F2F1F9] hover:[&_.menu-toggle-icon]:text-[#4F31FF]",
        className
      )}
      {...buttonProps}
    >
      {children}
    </button>
  );
});

function MenuItem({
  className,
  ...props
}: MenuItemLinkProps & { className?: string }) {
  return (
    <MenuItemLink
      className={cn(
        "menu-item relative flex h-7 w-full shrink-0 items-center gap-2 rounded-[4px] bg-transparent px-3 py-1 text-[#676571] no-underline transition-[width,padding,color,background-color,border-radius,box-shadow] duration-[var(--menu-transition-duration)] ease-[var(--menu-transition-ease)] [text-decoration:none] focus:[text-decoration:none] active:[text-decoration:none] visited:[text-decoration:none] motion-reduce:transition-none [&_.menu-label]:text-[#676571] [&_.menu-icon_svg]:max-h-[18px] [&_.menu-icon_svg]:max-w-[18px] [&_.menu-icon_svg]:overflow-visible [&_.menu-icon_svg]:text-[#676571] [&_.menu-icon_svg]:transition-colors [&_.menu-icon_svg_path]:transition-all [&_.menu-font-awesome-icon]:text-[#676571] [&_[data-id='labels-icon']]:h-[18px] [&_[data-id='labels-icon']]:w-[18px] [&.active]:bg-white [&.active]:text-[#1a1823] [&.active]:shadow-[0_1px_2px_rgba(16,24,40,0.05)] [&.active_.menu-label]:text-[#1a1823] [&.active_.menu-icon_svg]:text-[#4F31FF] [&.active_.menu-font-awesome-icon]:text-[#4F31FF] [&.active_.menu-upsell-badge]:bg-white [&.active_.menu-upsell-icon]:text-[#4F31FF] max-[667px]:h-8 max-[667px]:[&_.menu-icon_svg]:max-h-4 max-[667px]:[&_.menu-icon_svg]:max-w-4",
        className
      )}
      {...props}
    />
  );
}

/*
 * `className` is MERGED rather than overwritten. It used to sit after the
 * spread, which silently discarded any class a caller passed: the campaign
 * entry's week 3 red escalation set one and nothing happened. `Home` passes
 * none, so nothing about it changes.
 */
function HomeMenuItem({
  className,
  ...props
}: MenuItemLinkProps & { className?: string }) {
  return (
    <MenuItem
      {...props}
      className={cn("menu-home-item rounded-lg", className)}
    />
  );
}

function SubMenuItem(props: MenuItemLinkProps) {
  return (
    <MenuItem
      {...props}
      className="menu-sub-item mr-0 gap-[18px] rounded-lg px-2 pl-[22px] before:pointer-events-none before:absolute before:bottom-0 before:left-[22px] before:top-0 before:w-px before:bg-neutral-200 before:content-[''] [&_.menu-label]:text-sm [&_.menu-label]:leading-5 [&_.menu-icon_svg]:max-h-4 [&_.menu-icon_svg]:max-w-4"
    />
  );
}

function ParentMenuItem(props: MenuItemLinkProps) {
  return <MenuItem {...props} className="menu-parent-item" />;
}

const menuWrapperClassName = "menu-wrapper relative z-[1040] shrink-0";

function MenuWrapper({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(menuWrapperClassName, "menu-uplift", className)}
      {...props}
    >
      {children}
    </div>
  );
}
